package com.evoapps.evoshift.ui.user

import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.compose.foundation.layout.Box
import com.evoapps.evoshift.ui.theme.animateItemIfMotion
import com.evoapps.evoshift.ui.adaptive.LocalWindowWidthClass
import com.evoapps.evoshift.widgets.EvoTopBar
import com.evoapps.evoshift.widgets.TopBarAction
import com.evoapps.evoshift.widgets.helpAction
import com.evoapps.evoshift.ui.notifications.notificationsAction
import com.evoapps.evoshift.domain.usecase.ReportAbsenceUseCase
import com.evoapps.evoshift.ui.util.HelpCentreButton
import com.evoapps.evoshift.ui.util.HelpPage
import com.evoapps.evoshift.viewmodels.Gate
import com.evoapps.evoshift.viewmodels.shows
import com.evoapps.evoshift.widgets.parseIsoDate
import com.evoapps.evoshift.widgets.EvoTextField
import com.evoapps.evoshift.widgets.EvoRadioGroup
import com.evoapps.evoshift.widgets.EvoDateRangeField
import com.evoapps.evoshift.widgets.MissingYear
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.DialogSection
import com.evoapps.evoshift.widgets.DialogAction
import androidx.compose.ui.text.input.ImeAction
import evoshift.composeapp.generated.resources.absences_action_approve
import evoshift.composeapp.generated.resources.absences_action_cancel
import evoshift.composeapp.generated.resources.absences_action_reject
import evoshift.composeapp.generated.resources.absences_action_submit
import evoshift.composeapp.generated.resources.absences_active_requests
import evoshift.composeapp.generated.resources.absences_empty
import evoshift.composeapp.generated.resources.absences_end_date
import evoshift.composeapp.generated.resources.absences_history
import evoshift.composeapp.generated.resources.absences_reason_hint
import evoshift.composeapp.generated.resources.absences_sick_pay_full
import com.evoapps.evoshift.widgets.EvoSwitchRow
import evoshift.composeapp.generated.resources.absences_reason_privacy
import evoshift.composeapp.generated.resources.absences_request_new
import evoshift.composeapp.generated.resources.absences_start_date
import evoshift.composeapp.generated.resources.absences_title
import evoshift.composeapp.generated.resources.absences_type_label
import evoshift.composeapp.generated.resources.Res
import com.evoapps.evoshift.ui.util.label
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.ui.adaptive.AdaptiveCardGrid
import com.evoapps.evoshift.ui.adaptive.DetailPane
import com.evoapps.evoshift.ui.adaptive.ListDetailLayout
import com.evoapps.evoshift.ui.adaptive.PanePlaceholder
import com.evoapps.evoshift.ui.adaptive.showsDetailPane
import androidx.compose.foundation.border
import androidx.compose.material.icons.filled.EventBusy
import androidx.compose.runtime.saveable.rememberSaveable
import evoshift.composeapp.generated.resources.wide_pick_absence
import evoshift.composeapp.generated.resources.absences_status
import evoshift.composeapp.generated.resources.absences_period
import evoshift.composeapp.generated.resources.absences_reason
import evoshift.composeapp.generated.resources.absences_attachment
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.grid.GridItemSpan
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.Absence
import com.evoapps.evoshift.domain.model.AbsenceStatus
import com.evoapps.evoshift.domain.model.AbsenceType
import com.evoapps.evoshift.domain.model.WorkRequest
import androidx.compose.ui.platform.testTag
import com.evoapps.evoshift.domain.model.LeaveBalance
import com.evoapps.evoshift.ui.Cream
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.GoldMetallicBrush
import com.evoapps.evoshift.viewmodels.AbsencesEvent
import com.evoapps.evoshift.viewmodels.AbsencesViewModel
import com.evoapps.evoshift.widgets.LoadingOverlay
import com.evoapps.evoshift.widgets.RegularCard
import evoshift.composeapp.generated.resources.*
import org.jetbrains.compose.resources.stringResource

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AbsencesScreen(
    viewModel: AbsencesViewModel,
    /** Null at a tab's root: back returns home there, no arrow. */
    onNavigateBack: (() -> Unit)?,
) {
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    var showAddDialog by remember { mutableStateOf(false) }
    // SH-105: wide windows show the chosen absence beside the list.
    val paneMode = LocalWindowWidthClass.current.showsDetailPane
    var selectedId by rememberSaveable { mutableStateOf<String?>(null) }
    val selected = selectedId?.let { id -> (state.activeAbsences + state.pastAbsences).firstOrNull { it.id == id } }
    val snackbarHostState = remember { SnackbarHostState() }

    // A change whose save failed is undone on the list; this says why.
    val errorText = state.error?.asString()
    LaunchedEffect(errorText) {
        errorText?.let {
            snackbarHostState.showSnackbar(it)
            viewModel.onEvent(AbsencesEvent.ClearError)
        }
    }

    Scaffold(
        containerColor = Cream,
        topBar = {
            EvoTopBar(
                title = stringResource(Res.string.absences_title),
                onBack = onNavigateBack,
                showWorkplace = true,
                actions = listOf(helpAction(HelpPage.ABSENCES))
            )
        },
        snackbarHost = { SnackbarHost(snackbarHostState) },
        floatingActionButton = {
            // Reporting one's own absence; managers decide the team's instead. Nothing once the plan ended.
            if (state.capabilities.shows(Gate.REPORT_ABSENCE) && !state.capabilities.shows(Gate.TEAM_REQUESTS)) {
                FloatingActionButton(
                    onClick = { showAddDialog = true },
                    containerColor = DarkGold,
                    contentColor = DeepNavy
                ) {
                    Icon(Icons.Default.Add, contentDescription = null)
                }
            }
        }
    ) { padding ->
        val maxWidth = LocalWindowWidthClass.current.wideContentMaxWidth
        Box(modifier = Modifier.fillMaxSize().padding(padding)) {
            // SH-113: the person's own leave balance (managers see the team's on the Team screen).
            val leaveBalance = state.leaveBalance?.takeIf { (it.isKnown || it.hasAnything) && !state.capabilities.shows(Gate.TEAM_REQUESTS) }
            val nothing = state.activeAbsences.isEmpty() && state.pastAbsences.isEmpty() && state.workRequests.isEmpty()
            if (nothing && !state.isLoading && leaveBalance == null) {
                Box(Modifier.fillMaxSize().padding(16.dp), contentAlignment = Alignment.Center) {
                    Text(stringResource(Res.string.absences_empty), color = DeepNavy.copy(alpha = 0.5f))
                }
            }
            val isManager = state.capabilities.shows(Gate.TEAM_REQUESTS)
            val canChange = state.capabilities.shows(if (isManager) Gate.DECIDE_REQUESTS else Gate.REPORT_ABSENCE)
            ListDetailLayout(
                selected = selected != null,
                onClose = { selectedId = null },
                maxWidth = maxWidth,
                placeholder = { PanePlaceholder(stringResource(Res.string.wide_pick_absence), Icons.Default.EventBusy) },
                detail = {
                    selected?.let { absence ->
                        AbsenceDetailPane(
                            absence = absence,
                            isManager = isManager,
                            canChange = canChange,
                            onClose = { selectedId = null },
                            onStatusUpdate = { status -> viewModel.onEvent(AbsencesEvent.UpdateStatus(absence, status)) },
                            onDelete = {
                                viewModel.onEvent(AbsencesEvent.DeleteAbsence(absence.id))
                                selectedId = null
                            }
                        )
                    }
                },
                list = {
            // One column on phones; on tablets and desktops the absences sit side by side, with
            // the section headers across the full width.
            AdaptiveCardGrid(
                modifier = Modifier.fillMaxSize(),
                spacing = 16.dp,
                contentPadding = PaddingValues(16.dp),
                maxWidth = maxWidth
            ) {
                if (leaveBalance != null) {
                    item(span = { GridItemSpan(maxLineSpan) }) { LeaveBalanceCard(leaveBalance) }
                }
                // SH-223: the person's yearly limits (childcare, force majeure, carer's leave, overtime to take).
                // Only once something counts against them (the request form always shows its counter).
                val limitsYear = com.evoapps.evoshift.domain.util.AppClock.today().year
                if (!isManager && state.capabilities.shows(Gate.OWN_ABSENCES) && state.hasLimitsInUse(limitsYear)) {
                    item(span = { GridItemSpan(maxLineSpan) }) { RequestLimitsCard(state, limitsYear) }
                }
                if (nothing && !state.isLoading && leaveBalance != null) {
                    item(span = { GridItemSpan(maxLineSpan) }) {
                        Text(stringResource(Res.string.absences_empty), color = DeepNavy.copy(alpha = 0.5f), modifier = Modifier.padding(vertical = 16.dp))
                    }
                }
                if (state.activeAbsences.isNotEmpty() || state.activeWork.isNotEmpty()) {
                    item(span = { GridItemSpan(maxLineSpan) }) {
                        Text(
                            stringResource(Res.string.absences_active_requests),
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = FontWeight.Bold,
                            color = DeepNavy
                        )
                    }
                    items(state.activeAbsences, key = { "active-${it.id}" }) { absence ->
                        Box(Modifier.animateItemIfMotion(this), propagateMinConstraints = true) {
                            AbsenceItem(
                                absence = absence,
                                isManager = isManager,
                                // Beside the pane the cards only choose; the actions are in the pane.
                                canChange = canChange && !paneMode,
                                onStatusUpdate = { status -> viewModel.onEvent(AbsencesEvent.UpdateStatus(absence, status)) },
                                onDelete = { viewModel.onEvent(AbsencesEvent.DeleteAbsence(absence.id)) },
                                onClick = if (paneMode) { { selectedId = absence.id } } else null,
                                selected = paneMode && absence.id == selectedId
                            )
                        }
                    }
                }

                // SH-223: remote work, work events and overtime still to come or waiting.
                items(state.activeWork, key = { "work-${it.id}" }) { request ->
                    Box(Modifier.animateItemIfMotion(this), propagateMinConstraints = true) {
                        WorkRequestItem(request, canCancel = canChange && !isManager) { viewModel.onEvent(AbsencesEvent.CancelWorkRequest(request.id)) }
                    }
                }

                if (state.pastAbsences.isNotEmpty() || state.pastWork.isNotEmpty()) {
                    item(span = { GridItemSpan(maxLineSpan) }) {
                        Column {
                            Spacer(Modifier.height(16.dp))
                            Text(
                                stringResource(Res.string.absences_history),
                                style = MaterialTheme.typography.titleMedium,
                                fontWeight = FontWeight.Bold,
                                color = DeepNavy.copy(alpha = 0.7f)
                            )
                        }
                    }
                    items(state.pastWork, key = { "past-work-${it.id}" }) { request ->
                        Box(Modifier.animateItemIfMotion(this), propagateMinConstraints = true) { WorkRequestItem(request, canCancel = false) {} }
                    }
                    items(state.pastAbsences, key = { "past-${it.id}" }) { absence ->
                        Box(Modifier.animateItemIfMotion(this), propagateMinConstraints = true) {
                            AbsenceItem(
                                absence = absence,
                                isManager = isManager,
                                // Beside the pane the cards only choose; the actions are in the pane.
                                canChange = canChange && !paneMode,
                                onStatusUpdate = { status -> viewModel.onEvent(AbsencesEvent.UpdateStatus(absence, status)) },
                                onDelete = { viewModel.onEvent(AbsencesEvent.DeleteAbsence(absence.id)) },
                                onClick = if (paneMode) { { selectedId = absence.id } } else null,
                                selected = paneMode && absence.id == selectedId
                            )
                        }
                    }
                }
            }

                }
            )

            // Refreshes keep the list on screen; the spinner is only for the first load.
            LoadingOverlay(isLoading = state.isLoading && state.activeAbsences.isEmpty() && state.pastAbsences.isEmpty())
        }
    }

    if (showAddDialog) {
        // SH-223: every request type, remote work and the other work requests too.
        NewRequestDialog(
            state = state,
            balance = state.leaveBalance,
            onAddPlace = { viewModel.onEvent(AbsencesEvent.AddPlace(it)) },
            onAddedPlaceConsumed = { viewModel.onEvent(AbsencesEvent.ConsumeAddedPlace) },
            onDismiss = { showAddDialog = false },
            onConfirm = { request ->
                viewModel.onEvent(
                    when (request) {
                        is NewRequest.Leave -> AbsencesEvent.CreateRequest(request.draft)
                        is NewRequest.Work -> AbsencesEvent.CreateWorkRequest(request.draft)
                    }
                )
                showAddDialog = false
            }
        )
    }
}

@Composable
fun AbsenceItem(
    absence: Absence,
    isManager: Boolean,
    /** False: read-only (the workplace's plan ended, SH-65), the absence without actions. */
    canChange: Boolean = true,
    onStatusUpdate: (AbsenceStatus) -> Unit,
    onDelete: () -> Unit,
    /** SH-105: wide windows: the card chooses the absence for the pane beside the list. */
    onClick: (() -> Unit)? = null,
    selected: Boolean = false
) {
    RegularCard(
        onClick = onClick,
        modifier = Modifier
            .fillMaxWidth()
            .then(if (selected) Modifier.border(2.dp, DarkGold, RoundedCornerShape(20.dp)) else Modifier)
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Column(Modifier.weight(1f)) {
                    Text(
                        text = absence.type.label(),
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold,
                        color = DeepNavy
                    )
                    Text(
                        text = absencePeriod(absence),
                        style = MaterialTheme.typography.bodySmall,
                        color = DeepNavy.copy(alpha = 0.6f)
                    )
                    AbsenceDetailsLines(absence, DeepNavy.copy(alpha = 0.7f))
                }
                
                AbsenceStatusBadge(absence.status)
            }

            val reason = absence.reason
            if (!reason.isNullOrBlank()) {
                Spacer(Modifier.height(8.dp))
                Text(
                    text = reason,
                    style = MaterialTheme.typography.bodyMedium,
                    color = DeepNavy.copy(alpha = 0.8f)
                )
            }

            // SH-164: why the manager said no (when they said).
            val rejection = absence.rejectionReason
            if (absence.status == AbsenceStatus.REJECTED && !rejection.isNullOrBlank()) {
                Spacer(Modifier.height(8.dp))
                Text(
                    text = stringResource(Res.string.absences_rejection_reason, rejection),
                    style = MaterialTheme.typography.bodyMedium,
                    color = Color(0xFFC62828)
                )
            }

            AbsenceActions(absence, isManager, canChange, onStatusUpdate, onDelete)
        }
    }
}

/** Approve / reject (managers) or withdraw (the person) a pending absence; nothing when [canChange] is false. */
@Composable
private fun AbsenceActions(
    absence: Absence,
    isManager: Boolean,
    canChange: Boolean,
    onStatusUpdate: (AbsenceStatus) -> Unit,
    onDelete: () -> Unit
) {
    Column {
            if (!canChange) {
                // Nothing to decide or withdraw.
            } else if (isManager && absence.status == AbsenceStatus.PENDING) {
                Spacer(Modifier.height(16.dp))
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    OutlinedButton(
                        onClick = { onStatusUpdate(AbsenceStatus.REJECTED) },
                        modifier = Modifier.weight(1f),
                        colors = ButtonDefaults.outlinedButtonColors(contentColor = Color.Red)
                    ) {
                        Text(stringResource(Res.string.absences_action_reject))
                    }
                    Button(
                        onClick = { onStatusUpdate(AbsenceStatus.APPROVED) },
                        modifier = Modifier.weight(1f),
                        colors = ButtonDefaults.buttonColors(containerColor = DarkGold, contentColor = DeepNavy)
                    ) {
                        Text(stringResource(Res.string.absences_action_approve))
                    }
                }
            } else if (!isManager && absence.status == AbsenceStatus.PENDING) {
                Spacer(Modifier.height(12.dp))
                TextButton(
                    onClick = onDelete,
                    colors = ButtonDefaults.textButtonColors(contentColor = Color.Red)
                ) {
                    Icon(Icons.Default.Delete, contentDescription = null, modifier = Modifier.size(16.dp))
                    Spacer(Modifier.width(4.dp))
                    Text(stringResource(Res.string.absences_action_cancel))
                }
            }
    }
}

/**
 * SH-105: the chosen absence beside the list on wide windows: its type, status, period, reason
 * and attachment, and the same actions as its card on a phone.
 */
@Composable
fun AbsenceDetailPane(
    absence: Absence,
    isManager: Boolean,
    canChange: Boolean,
    onClose: () -> Unit,
    onStatusUpdate: (AbsenceStatus) -> Unit,
    onDelete: () -> Unit
) {
    DetailPane(
        title = absence.type.label(),
        subtitle = "${absence.startDate} - ${absence.endDate}",
        icon = Icons.Default.EventBusy,
        onClose = onClose,
        dark = false,
        modifier = Modifier.fillMaxSize()
    ) {
        AbsenceFact(stringResource(Res.string.absences_status)) { AbsenceStatusBadge(absence.status) }
        AbsenceFact(stringResource(Res.string.absences_period)) {
            Text(absencePeriod(absence), style = MaterialTheme.typography.bodyLarge, color = DeepNavy)
            AbsenceDetailsLines(absence, DeepNavy.copy(alpha = 0.7f))
        }
        absence.reason?.takeIf { it.isNotBlank() }?.let { reason ->
            AbsenceFact(stringResource(Res.string.absences_reason)) {
                Text(reason, style = MaterialTheme.typography.bodyLarge, color = DeepNavy)
            }
        }
        // SH-164: why the manager said no.
        absence.rejectionReason?.takeIf { absence.status == AbsenceStatus.REJECTED && it.isNotBlank() }?.let { rejection ->
            Text(stringResource(Res.string.absences_rejection_reason, rejection), style = MaterialTheme.typography.bodyLarge, color = Color(0xFFC62828))
        }
        absence.attachmentUrl?.takeIf { it.isNotBlank() }?.let { url ->
            AbsenceFact(stringResource(Res.string.absences_attachment)) {
                Text(url, style = MaterialTheme.typography.bodyMedium, color = DeepNavy.copy(alpha = 0.8f), maxLines = 2)
            }
        }
        AbsenceActions(absence, isManager, canChange, onStatusUpdate, onDelete)
    }
}

@Composable
private fun AbsenceFact(label: String, value: @Composable () -> Unit) {
    Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
        Text(label, style = MaterialTheme.typography.labelMedium, color = DeepNavy.copy(alpha = 0.6f))
        value()
    }
}

@Composable
fun AbsenceStatusBadge(status: AbsenceStatus) {
    val color = when (status) {
        AbsenceStatus.PENDING -> Color(0xFFFFB300)
        AbsenceStatus.APPROVED -> Color(0xFF4CAF50)
        AbsenceStatus.REJECTED -> Color(0xFFF44336)
    }
    
    Box(
        modifier = Modifier
            .clip(RoundedCornerShape(12.dp))
            .background(color.copy(alpha = 0.1f))
            .padding(horizontal = 8.dp, vertical = 4.dp)
    ) {
        Text(
            text = status.label(),
            style = MaterialTheme.typography.labelSmall,
            color = color,
            fontWeight = FontWeight.Bold
        )
    }
}

/** SH-223: an absence's period: "start - end", or "date, 08:00–12:00" for part of a day. */
fun absencePeriod(absence: Absence): String =
    if (absence.isHourly) "${absence.startDate}, ${absence.startTime}–${absence.endTime}" else "${absence.startDate} - ${absence.endDate}"

/** SH-223: a work request's period, the same way. */
fun workPeriod(request: WorkRequest): String =
    if (request.isHourly) {
        if (request.startDate == request.endDate) "${request.startDate}, ${request.startTime}–${request.endTime}"
        else "${request.startDate} - ${request.endDate}, ${request.startTime}–${request.endTime}"
    } else "${request.startDate} - ${request.endDate}"

/** SH-223: the details a request type adds: the occasion, whom the care is for, the manager's override note. */
@Composable
fun AbsenceDetailsLines(absence: Absence, color: Color) {
    absence.occasion?.let { Text(stringResource(it.labelRes()), style = MaterialTheme.typography.bodySmall, color = color) }
    absence.careFor?.let {
        Text(stringResource(if (it == com.evoapps.evoshift.domain.model.CareRecipient.CHILD) Res.string.care_for_child else Res.string.care_for_family),
            style = MaterialTheme.typography.bodySmall, color = color)
    }
    absence.overrideNote?.takeIf { it.isNotBlank() }?.let {
        Text(stringResource(Res.string.requests_override_shown, it), style = MaterialTheme.typography.bodySmall, color = color)
    }
}

/** SH-223: a remote-work, work-event or overtime request on the person's list: kind, period, place, status. */
@Composable
fun WorkRequestItem(request: WorkRequest, canCancel: Boolean, onCancel: () -> Unit) {
    RegularCard(modifier = Modifier.fillMaxWidth()) {
        Column(modifier = Modifier.padding(16.dp)) {
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                Column(Modifier.weight(1f)) {
                    Text(request.kind.label(), style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold, color = DeepNavy)
                    Text(workPeriod(request), style = MaterialTheme.typography.bodySmall, color = DeepNavy.copy(alpha = 0.6f))
                    request.placeName?.let { Text(it, style = MaterialTheme.typography.bodyMedium, color = DeepNavy.copy(alpha = 0.8f)) }
                }
                AbsenceStatusBadge(request.status)
            }
            request.note?.takeIf { it.isNotBlank() }?.let {
                Spacer(Modifier.height(8.dp))
                Text(it, style = MaterialTheme.typography.bodyMedium, color = DeepNavy.copy(alpha = 0.8f))
            }
            request.rejectionReason?.takeIf { request.status == AbsenceStatus.REJECTED && it.isNotBlank() }?.let {
                Spacer(Modifier.height(8.dp))
                Text(stringResource(Res.string.absences_rejection_reason, it), style = MaterialTheme.typography.bodyMedium, color = Color(0xFFC62828))
            }
            if (canCancel && request.status == AbsenceStatus.PENDING) {
                Spacer(Modifier.height(12.dp))
                TextButton(onClick = onCancel, colors = ButtonDefaults.textButtonColors(contentColor = Color.Red)) {
                    Icon(Icons.Default.Delete, contentDescription = null, modifier = Modifier.size(16.dp))
                    Spacer(Modifier.width(4.dp))
                    Text(stringResource(Res.string.absences_action_cancel))
                }
            }
        }
    }
}

/** Test tag of the yearly limits card. */
const val REQUEST_LIMITS_TAG = "request_limits_card"

/**
 * SH-223: the person's yearly limits: childcare (art. 188), force majeure, carer's leave, and the
 * overtime still to take as time off (when there is any).
 */
@Composable
fun RequestLimitsCard(state: com.evoapps.evoshift.viewmodels.AbsencesUiState, year: Int) {
    val rows = buildList {
        listOf(AbsenceType.CHILDCARE, AbsenceType.FORCE_MAJEURE, AbsenceType.CARER_LEAVE).forEach { type ->
            state.counter(type, year)?.takeIf { it.used + it.pending > 0 }?.let { add(type to it) }
        }
    }
    val overtime = state.overtimeBalance?.takeIf { it.limit > 0 }
    RegularCard(modifier = Modifier.fillMaxWidth().testTag(REQUEST_LIMITS_TAG)) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            rows.forEach { (type, counter) ->
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                    Text(stringResource(Res.string.counter_title, type.label(), year), style = MaterialTheme.typography.bodyMedium, color = DeepNavy,
                        modifier = Modifier.weight(1f))
                    Text(counterLeftText(counter), style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.Bold, color = DeepNavy)
                }
            }
            overtime?.let {
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                    Text(stringResource(Res.string.counter_overtime_title), style = MaterialTheme.typography.bodyMedium, color = DeepNavy, modifier = Modifier.weight(1f))
                    Text(stringResource(Res.string.counter_overtime_left, amountText(it.remaining), amountText(it.limit)),
                        style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.Bold, color = DeepNavy)
                }
            }
        }
    }
}
